/**
 * Downloads a file from a blob
 * @param blob - The blob to download
 * @param filename - The filename to save as
 */
export function downloadBlob(blob: Blob, filename: string): void {
  // Validate blob
  if (!blob || !(blob instanceof Blob)) {
    throw new Error('Invalid blob provided for download');
  }

  // Validate blob size
  if (blob.size === 0) {
    throw new Error('Cannot download empty file');
  }

  // Validate filename
  if (!filename || typeof filename !== 'string' || filename.trim() === '') {
    throw new Error('Invalid filename provided');
  }

  const blobUrl = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the blob URL
  setTimeout(() => {
    window.URL.revokeObjectURL(blobUrl);
  }, 100);
}

/**
 * Gets the file extension from an asset's file_path or content_type.
 * Falls back to '.docx' if neither provides a usable extension.
 */
export function getAssetFileExtension(filePath?: string, contentType?: string): string {
  if (filePath) {
    const lastDot = filePath.lastIndexOf('.');
    if (lastDot !== -1) {
      return filePath.substring(lastDot);
    }
  }
  const mimeToExt: Record<string, string> = {
    'application/pdf': '.pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'application/vnd.ms-excel': '.xls',
    'text/plain': '.txt',
    'text/html': '.html',
  };
  if (contentType && mimeToExt[contentType]) {
    return mimeToExt[contentType];
  }
  return '.docx';
}

/**
 * Downloads a document from an asset service by asset ID
 * @param downloadFn - Async function that downloads the blob
 * @param filename - The filename to save as
 * @throws Error if download fails
 */
export async function downloadDocument(
  downloadFn: () => Promise<Blob>,
  filename: string,
): Promise<void> {
  try {
    const blob = await downloadFn();
    downloadBlob(blob, filename);
  } catch (error) {
    console.error('Failed to download document:', error);
    throw error;
  }
}
